import type React from "react"
import { useEffect, useState } from "react"

/**
 * State for a tooltip, provides `isOpen` as a boolean
 */
interface TooltipState {
    isOpen: boolean
}

/**
 * `useTooltip` is a react hook that manages the open and closed state of a tooltip element.
 *
 * @example
 * Here's a minimal example for usage.
 * ```jsx
 * const TooltipComponent = (props) => {
 *     const ref = useRef(null)
 *     const { isOpen } = useTooltip(ref)
 *
 *     return (
 *       <>
 *         <div ref={ref}>I have a tooltip!</div>
 *         {isOpen && <div class="tooltip">And I'm the tooltip that will appear on hover!</div>}
 *       </>
 *     )
 * }
 * ```
 *
 * @param ref A **required** reference to an html element that will have the event handlers attached to it.
 */
export function useTooltip(
    ref: React.RefObject<HTMLElement | SVGSVGElement>,
): TooltipState {
    const [isOpen, setIsOpen] = useState<boolean>(false)

    const handleEnter = () => {
        setIsOpen(true)
    }

    const handleLeave = () => {
        setIsOpen(false)
    }

    const handleOutsideClick = (e: MouseEvent | TouchEvent) => {
        if (!ref.current?.contains(e.target as Node)) {
            setIsOpen(false)
        } else {
            setIsOpen(true)
        }
    }

    useEffect(() => {
        const instance = ref.current

        instance?.addEventListener("mouseenter", handleEnter)
        instance?.addEventListener("mouseleave", handleLeave)
        document.addEventListener("pointerdown", handleOutsideClick)
        document.addEventListener("keydown", handleLeave)
        document.addEventListener("scroll", handleLeave)

        const observer = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) {
                // This hides the tooltip if the tooltip goes out of view: e.g. switching to different tab via keyboard
                // We have a keydown listener but it will not capture tab key because of our global key handlers.
                handleLeave()
            }
        })

        if (instance) {
            observer.observe(instance as Element)
        }

        return () => {
            instance?.removeEventListener("mouseenter", handleEnter)
            instance?.removeEventListener("mouseleave", handleLeave)
            document.removeEventListener("pointerdown", handleOutsideClick)
            document.removeEventListener("keydown", handleLeave)
            document.removeEventListener("scroll", handleLeave)
            observer.disconnect()
        }
    }, [ref])

    return {
        isOpen: isOpen,
    }
}
